import { ref, computed } from '@vue/composition-api'
import store from '@/store'
import i18n from '@/libs/i18n'
import userStoreModule from './userStoreModule'

const USER_APP_STORE_MODULE_NAME = 'app-user'
const HISTORY_ROLES = ['TRCKOWN', 'SYSADMIN']

const ensureUserStoreModule = () => {
  if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) {
    store.registerModule(USER_APP_STORE_MODULE_NAME, userStoreModule)
  }
}

export const canViewUserHistory = user => {
  if (!user) return false
  return HISTORY_ROLES.includes(user.role)
}

const normalizeHistoryItem = item => ({
  userId: item.userId ?? item.UserId,
  userName: item.userName ?? item.UserName,
  action: item.action ?? item.Action,
  changedBy: item.changedBy ?? item.ChangedBy,
  changedByName: item.changedByName ?? item.ChangedByName,
  changedAt: item.changedAt ?? item.ChangedAt,
})

export default function useUserHistory() {
  const items = ref([])
  const isBusy = ref(false)
  const currentPage = ref(1)
  const perPage = ref(10)
  const totalRows = ref(0)
  const userId = ref(null)
  const userName = ref('')

  const dataMeta = computed(() => {
    let from = (currentPage.value - 1) * perPage.value + 1
    let to = currentPage.value * perPage.value

    if (to > totalRows.value) to = totalRows.value
    if (totalRows.value === 0) from = 0

    return { from, to, of: totalRows.value }
  })

  const modalTitle = computed(() => {
    if (userName.value) {
      return `${i18n.t('User History')} — ${userName.value}`
    }
    return i18n.t('User History')
  })

  const fields = computed(() => [
    { key: 'changedAt', label: i18n.t('Date'), sortable: true },
    { key: 'action', label: i18n.t('Action'), sortable: false },
    { key: 'changedByName', label: i18n.t('Changed By'), sortable: true },
  ])

  const reset = () => {
    userId.value = null
    userName.value = ''
    items.value = []
    totalRows.value = 0
    currentPage.value = 1
    isBusy.value = false
  }

  const fetchHistory = (page = 1) => {
    if (!userId.value) return Promise.resolve()

    ensureUserStoreModule()

    isBusy.value = true
    currentPage.value = page

    const dispatchResult = store.dispatch('app-user/fetchUserHistory', {
      userId: userId.value,
      page: currentPage.value,
      perPage: perPage.value,
    })

    if (!dispatchResult || typeof dispatchResult.then !== 'function') {
      isBusy.value = false
      return Promise.reject(new Error('fetchUserHistoryFailed'))
    }

    return dispatchResult
      .then(response => {
        const rawItems = response.data.data || response.data.Data || []
        items.value = rawItems.map(normalizeHistoryItem)
        totalRows.value = response.data.totalItems ?? response.data.TotalItems ?? 0
      })
      .catch(() => {
        items.value = []
        totalRows.value = 0
        throw new Error('fetchUserHistoryFailed')
      })
      .finally(() => {
        isBusy.value = false
      })
  }

  const openForUser = user => {
    if (!user?.id) return Promise.resolve()

    userId.value = user.id
    userName.value = user.name || user.email || ''
    currentPage.value = 1

    return fetchHistory(1)
  }

  return {
    items,
    isBusy,
    currentPage,
    perPage,
    totalRows,
    dataMeta,
    modalTitle,
    fields,
    fetchHistory,
    openForUser,
    reset,
  }
}
